import {
  Badge,
  DropdownPanel,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  SubmenuMenuOption,
  Tooltip,
  TooltipButton,
  Text,
} from "@smartsuite/react-ui/lib";
import * as R from "remeda";
import type React from "react";
import { useCallback, useRef } from "react";
import type { MutableRefObject } from "react";
import { StyledTriggerRequest, betaStyle, BetaWrapper } from "./styles";
import { useRootStore } from "../../stores/hooks";
import {
  integrationsIntercomIds,
  intergrationsMainIntercomId,
  isSmartSuiteIntegration,
} from "../../utils/constants";
import { t } from "i18next";
import {
  capitalize,
  translateIfPresent,
  translateTrigger,
} from "../../locales/i18n";
import * as S from "../../utils/dropdown";
import { getIntegrationLogo } from "../../utils/integration-logo";
import { observer } from "mobx-react-lite";
import { capitalizeFirstLetter } from "../../utils/strings";
import { DropdownOnboardingHelp } from "../DropdownOnboardingHelp";
import { getLocalCssColorVariables } from "../../utils/cssVariablesUtils";
import { InformationTooltip } from "../InformationTooltip";
import {
  intercomOpenAutomationTriggersArticle,
  intercomOpenIntegrationsArticle,
} from "../../services/intercom.service";
import { commingSoonStyle, MenuOptionList } from "../ActionMenu/styles";
import { useGetSSTriggerLogo } from "../../utils/hooks";
import { Availability } from "@smartsuite/automation-engine-api";
import * as Style from "../ActionMenu/styles";
import { globalModalsZIndex } from "@smartsuite/react-ui/lib/constants/zIndex";
import { GrayColors } from "@smartsuite/types";

const zIndex = globalModalsZIndex + 1;

interface TriggerMenuProps {
  menuButtonRef: React.MutableRefObject<HTMLElement | null>;
  isMenuOpen: boolean;
  setMenuOpen: (isOpen: boolean) => void;
  onTriggerChange: (integrationId: string, triggerId: string) => void;
  showOnboarding?: boolean;
  dropdownMaxWidth?: number;
  dropdownMinWidth?: number;
}

export const TriggerMenu = observer<TriggerMenuProps>(
  ({
    menuButtonRef,
    isMenuOpen,
    setMenuOpen,
    onTriggerChange,
    showOnboarding,
    dropdownMaxWidth,
    dropdownMinWidth,
  }) => {
    const store = useRootStore();
    const integrations = store.integrations.list.filter(
      (integration) =>
        integration.info?.availability !== Availability.AVAILABILITY_DISABLED,
    );
    const ssIntegrations = integrations.filter((integration) =>
      isSmartSuiteIntegration(integration.integrationId),
    );
    const integrationWithTriggers = integrations.filter((integration) => {
      return (
        integration.triggers.length > 0 &&
        !isSmartSuiteIntegration(integration.integrationId)
      );
    });

    const getSSTriggerLogo = useGetSSTriggerLogo();

    const dropdownPanelRef = useRef<HTMLDivElement>(null);

    const onClickRequest = useCallback(() => {
      window.open("https://feedback.smartsuite.com/");
    }, []);

    return (
      <DropdownPanel
        dropdownMaxWidth={dropdownMaxWidth}
        minWidth={dropdownMinWidth}
        zIndex={zIndex}
        className="dropdown-panel"
        enableWidthCalculation={true}
        targetElementRef={
          menuButtonRef as unknown as MutableRefObject<HTMLElement>
        }
        isActive={isMenuOpen}
        portalStyles={getLocalCssColorVariables(store.theme)}
        onClickOutside={() => setMenuOpen(false)}
        my="left top+2"
      >
        {R.isTruthy(showOnboarding) && (
          <DropdownOnboardingHelp
            helpText={t("dropdown-help-onboarding.trigger-help-text")}
            variableName="hide-trigger-help-onboarding"
          />
        )}
        <S.MenuWrapper>
          <MenuOptionWrapper maxHeight={507}>
            {/* SS integration trigger list */}
            {ssIntegrations.length > 0 && (
              <>
                <S.IntegrationTitle>
                  <InformationTooltip
                    tooltipText={t("general.trigger-smartsuite-tooltip-text")}
                    onClick={() => {
                      intercomOpenAutomationTriggersArticle();
                    }}
                  >
                    {`SmartSuite ${capitalizeFirstLetter(
                      t("general.trigger.other"),
                    )}`}
                  </InformationTooltip>
                </S.IntegrationTitle>

                <MenuOptionList>
                  {ssIntegrations.map((ssIntegration) =>
                    ssIntegration.triggers.map((trigger) => (
                      <MenuOption
                        className="with-info-tooltip"
                        key={trigger.triggerId}
                        {...getSSTriggerLogo(trigger.triggerId)}
                        onClick={() => {
                          if (
                            trigger.info?.availability ===
                            Availability.COMMING_SOON
                          ) {
                            return;
                          }
                          onTriggerChange(
                            ssIntegration.integrationId,
                            trigger.triggerId,
                          );
                        }}
                        renderRightContent={
                          trigger.info?.availability !==
                            Availability.COMMING_SOON && (
                            <Tooltip
                              className="trigger-help-tooltip"
                              placements={["right", "left"]}
                              padding={2}
                              target={
                                <TooltipButton
                                  handleClick={(event) => {
                                    event.stopPropagation();
                                    intercomOpenAutomationTriggersArticle();
                                  }}
                                  iconName="info-filled"
                                  iconSize="S"
                                  colors={{
                                    default: GrayColors["G-4"],
                                    hover: GrayColors["G-8"],
                                  }}
                                />
                              }
                              renderTooltipContent={
                                <Text
                                  size="xs"
                                  weight="semibold"
                                  color="reverse"
                                >
                                  {translateIfPresent(
                                    `triggers.${
                                      trigger.info?.description ?? ""
                                    }`,
                                    trigger.info?.description,
                                  )}
                                </Text>
                              }
                            />
                          )
                        }
                        // TODO: Respove issues with popups
                        // popoverContainerClassName={"with-info-tooltip-popover"}
                      >
                        {translateTrigger(trigger)}
                        {(trigger.info?.beta === true ||
                          trigger.info?.availability === Availability.BETA) && (
                          <BetaWrapper>
                            <Badge styles={betaStyle}>BETA</Badge>
                          </BetaWrapper>
                        )}
                        {trigger.info?.availability ===
                          Availability.COMMING_SOON && (
                          <BetaWrapper>
                            <Badge styles={commingSoonStyle}>COMING SOON</Badge>
                          </BetaWrapper>
                        )}
                      </MenuOption>
                    )),
                  )}
                </MenuOptionList>
              </>
            )}

            {/* Integrations list */}
            {integrationWithTriggers.length > 0 ? (
              <S.IntegrationTitle pt={16}>
                <InformationTooltip
                  tooltipText={t("general.trigger-other-tooltip-text")}
                  onClick={() => {
                    intercomOpenAutomationTriggersArticle();
                  }}
                >
                  {capitalize(t("general.integration.other"))}
                </InformationTooltip>
              </S.IntegrationTitle>
            ) : null}
            <S.MenuWrapper ref={dropdownPanelRef}>
              {integrationWithTriggers.map((integration) => {
                return (
                  <SubmenuMenuOption
                    key={integration.integrationId}
                    name={integration.label}
                    my="left top"
                    at="right top-4"
                    parentMenu={dropdownPanelRef}
                    renderIcon={() => {
                      const Icon = getIntegrationLogo(
                        integration.integrationId,
                      );
                      return <Icon style={{ marginRight: "10px" }} />;
                    }}
                    autohide
                    delayShow
                    zIndex={1000}
                  >
                    <MenuOptionWrapper maxHeight={507} smallListPaddings>
                      <Style.MenuOptionList>
                        {integration.triggers.map((trigger) => (
                          <Style.MenuOptionWrapper key={trigger.triggerId}>
                            <MenuOption
                              key={trigger.triggerId}
                              renderIcon={() => {
                                const Icon = getIntegrationLogo(
                                  integration.integrationId,
                                );
                                return <Icon style={{ marginRight: "10px" }} />;
                              }}
                              onClick={() => {
                                if (
                                  trigger.info?.availability ===
                                  Availability.COMMING_SOON
                                ) {
                                  return;
                                }
                                setMenuOpen(false);
                                onTriggerChange(
                                  integration.integrationId,
                                  trigger.triggerId,
                                );
                              }}
                              className="submenu-item"
                              infoData={{
                                onIconClick: (event) => {
                                  event.stopPropagation();
                                  intercomOpenIntegrationsArticle(
                                    integrationsIntercomIds[
                                      trigger.triggerId
                                    ] || intergrationsMainIntercomId,
                                  );
                                },
                              }}
                            >
                              {translateTrigger(trigger)}
                              {(trigger.info?.beta === true ||
                                trigger.info?.availability ===
                                  Availability.BETA) && (
                                <BetaWrapper>
                                  <Badge styles={betaStyle}>BETA</Badge>
                                </BetaWrapper>
                              )}
                              {trigger.info?.availability ===
                                Availability.COMMING_SOON && (
                                <BetaWrapper>
                                  <Badge styles={Style.commingSoonStyle}>
                                    COMING SOON
                                  </Badge>
                                </BetaWrapper>
                              )}
                            </MenuOption>
                          </Style.MenuOptionWrapper>
                        ))}
                      </Style.MenuOptionList>
                    </MenuOptionWrapper>
                  </SubmenuMenuOption>
                );
              })}
            </S.MenuWrapper>
            <MenuLineSeparator />
            <StyledTriggerRequest>
              {t("automation-form.dont-see-trigger-you-need")}{" "}
              <button type="button" onClick={onClickRequest}>
                {t("automation-form.request")}
              </button>
            </StyledTriggerRequest>
          </MenuOptionWrapper>
        </S.MenuWrapper>
      </DropdownPanel>
    );
  },
);
